import { Form, FormSubmitButton, TextAreaFormField } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  retrieveMatterQueryOptions,
  useUpdateMatter,
} from "@/lib/data/react-query/matters/matter";
import { MatterSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { cn } from "@/lib/utils/cn";
import { modalFormFieldsetClassName } from "@/lib/utils/modal-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";

interface CaseInjuriesFormModalProps {
  matterId: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const formSchema = MatterSchema.pick({
  clientInjuryDescription: true,
  incidentInjuryDescription: true,
  clientVisibleBodilyInjury: true,
  clientPriorInjury: true,
  clientPriorInjuryDescription: true,
  clientPriorInjuryLegalRepresentation: true,
});

const defaultFormValues = {
  matterClientInjuryDescription: "",
  matterIncidentInjuryDescription: "",
  matterClientVisibileBodilyInjury: false,
  matterClientPriorInjury: false,
  matterClientPriorInjuryDescription: "",
  matterClientPriorInjuryLegalRepresentation: false,
};

const CASE_INJURIES_MODAL_FORM_ID = "case-injuries-modal-form";

export const CaseInjuriesFormModal = ({
  matterId,
  isOpen,
  setIsOpen,
}: CaseInjuriesFormModalProps) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;

  const updateMatter = useUpdateMatter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      ...defaultFormValues,
      ...matter,
    },
    mode: "onSubmit",
  });

  const handleSubmitInjuries = async (data: z.infer<typeof formSchema>) => {
    try {
      await updateMatter.mutateAsync({
        clientInjuryDescription: data.clientInjuryDescription,
        clientPriorInjury: data.clientPriorInjury,
        clientPriorInjuryDescription: data.clientPriorInjuryDescription,
        clientPriorInjuryLegalRepresentation:
          data.clientPriorInjuryLegalRepresentation,
        clientVisibleBodilyInjury: data.clientVisibleBodilyInjury,
        dateModified: matter.dateModified,
        id: matter.id,
        incidentInjuryDescription: data.incidentInjuryDescription,
      });
      setIsOpen(false);
    } catch {
      // Toast + rollback handled by useUpdateMatter; keep modal open
    }
  };

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const priorInjuryValue = form.watch("clientPriorInjury");

  const modalFooter = (
    <div className="flex w-full flex-wrap justify-end gap-2">
      <FormCancelButton setOpen={setIsOpen} isDirty={form.formState.isDirty} />
      <FormSubmitButton
        loading={updateMatter.isPending}
        submitFormId={CASE_INJURIES_MODAL_FORM_ID}
      />
    </div>
  );

  const fieldset = (
    <fieldset className={cn(modalFormFieldsetClassName, "my-0")}>
      <TextAreaFormField
        register={form.register("clientInjuryDescription")}
        label="Client injury description"
        className="col-span-full sm:col-span-2"
        textAreaClassName="field-sizing-content max-h-[20lh]"
      />
      <CheckboxFormField
        register={form.register("clientVisibleBodilyInjury")}
        label="Visible bodily injury"
      />
      <CheckboxFormField
        register={form.register("clientPriorInjury")}
        label="Client prior injury"
      />
      {priorInjuryValue && (
        <TextAreaFormField
          register={form.register("clientPriorInjuryDescription")}
          label="Prior injury description"
          className="col-span-full sm:col-span-2"
          textAreaClassName="field-sizing-content max-h-[20lh]"
        />
      )}
      {priorInjuryValue && (
        <CheckboxFormField
          register={form.register("clientPriorInjuryLegalRepresentation")}
          label="Prior injury legal representation"
        />
      )}
    </fieldset>
  );

  return (
    <Form {...form}>
      <AppModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title="Injuries"
        size="2xl"
        className="w-[calc(100vw-2rem)] max-w-4xl"
        loading={updateMatter.isPending}
        isDirty={form.formState.isDirty}
        footer={modalFooter}
      >
        <form
          id={CASE_INJURIES_MODAL_FORM_ID}
          className="relative flex min-h-0 flex-1 flex-col px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit(handleSubmitInjuries)();
          }}
        >
          {updateMatter.isPending && <FormLoadingOverlay />}
          {fieldset}
        </form>
      </AppModal>
    </Form>
  );
};
